import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

// Импорт страниц
const LandingPage = () => import('@/views/LandingPage.vue')
const LoginPage = () => import('@/views/auth/LoginPage.vue')
const RegisterPage = () => import('@/views/auth/RegisterPage.vue')
const DashboardPage = () => import('@/views/DashboardPage.vue')
const ProjectsPage = () => import('@/views/projects/ProjectsPage.vue')
const ProjectDetailsPage = () => import('@/views/projects/ProjectDetailsPage.vue')
const KeywordsPage = () => import('@/views/keywords/KeywordsPage.vue')
const ClustersPage = () => import('@/views/clusters/ClustersPage.vue')
const ExportsPage = () => import('@/views/exports/ExportsPage.vue')
const SettingsPage = () => import('@/views/settings/SettingsPage.vue')
const ProfilePage = () => import('@/views/ProfilePage.vue')
const NotFoundPage = () => import('@/views/NotFoundPage.vue')

// TDH страницы
const TdhProjectPage = () => import('@/views/tdh/TdhProjectPage.vue')
const TdhProjectDetailPage = () => import('@/views/tdh/TdhProjectDetailPage.vue')
const TdhAdminPage = () => import('@/views/tdh/TdhAdminPage.vue')
const TdhModelsPage = () => import('@/views/tdh/TdhModelsPage.vue')

// Макеты
const AuthLayout = () => import('@/layouts/AuthLayout.vue')
const AppLayout = () => import('@/layouts/AppLayout.vue')

const routes: Array<RouteRecordRaw> = [
  // Лендинг страница
  {
    path: '/',
    name: 'landing',
    component: LandingPage,
    meta: {
      title: 'TDH Promodo - Професійна обробка семантичного ядра',
      public: true
    }
  },

  // Аутентификация
  {
    path: '/auth',
    component: AuthLayout,
    meta: { public: true },
    children: [
      {
        path: 'login',
        name: 'login',
        component: LoginPage,
        meta: {
          title: 'Увійти - TDH Promodo'
        }
      },
      {
        path: 'register',
        name: 'register',
        component: RegisterPage,
        meta: {
          title: 'Реєстрація - TDH Promodo'
        }
      },
      {
        path: 'callback',
        name: 'google-callback',
        component: () => import('@/views/auth/GoogleCallback.vue'),
        meta: {
          title: 'Авторизація - TDH Promodo'
        }
      }
    ]
  },

  // Главное приложение
  {
    path: '/app',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      // Главная панель
      {
        path: '',
        name: 'dashboard',
        component: DashboardPage,
        meta: {
          title: 'Головна панель - TDH Promodo'
        }
      },

      // Проекты
      {
        path: 'projects',
        name: 'projects',
        component: ProjectsPage,
        meta: {
          title: 'Проекти - TDH Promodo'
        }
      },
      {
        path: 'projects/:id',
        name: 'project-details',
        component: ProjectDetailsPage,
        meta: {
          title: 'Деталі проекту - TDH Promodo'
        }
      },

      // Ключевые слова
      {
        path: 'projects/:projectId/keywords',
        name: 'keywords',
        component: KeywordsPage,
        meta: {
          title: 'Ключові слова - TDH Promodo'
        }
      },

      // Кластеры
      {
        path: 'projects/:projectId/clusters',
        name: 'clusters',
        component: ClustersPage,
        meta: {
          title: 'Кластери - TDH Promodo'
        }
      },

      // Экспорт
      {
        path: 'projects/:projectId/exports',
        name: 'exports',
        component: ExportsPage,
        meta: {
          title: 'Експорт - TDH Promodo'
        }
      },

      // Настройки
      {
        path: 'settings',
        name: 'settings',
        component: SettingsPage,
        meta: {
          title: 'Налаштування - TDH Promodo'
        }
      },

      // Профиль
      {
        path: 'profile',
        name: 'profile',
        component: ProfilePage,
        meta: {
          title: 'Профіль - TDH Promodo'
        }
      },

      // TDH Генератор
      {
        path: 'tdh',
        children: [
          {
            path: 'projects/:id',
            name: 'tdh-project-detail',
            component: TdhProjectPage,
            meta: {
              title: 'TDH Проект - TDH Promodo'
            }
          },
          {
            path: 'projects/:id/tasks',
            name: 'tdh-project-tasks',
            component: TdhProjectDetailPage,
            meta: {
              title: 'TDH Завдання - TDH Promodo'
            }
          },
          {
            path: 'admin',
            name: 'tdh-admin',
            component: TdhAdminPage,
            meta: {
              title: 'TDH Адміністрування - TDH Promodo',
              requiresAdmin: true
            }
          },
          {
            path: 'models',
            name: 'tdh-models',
            component: TdhModelsPage,
            meta: {
              title: 'Fine-Tuned Моделі - TDH Promodo'
            }
          }
        ]
      },

      // Статистика
      {
        path: 'statistics',
        name: 'statistics',
        component: () => import('@/views/StatisticsPage.vue'),
        meta: {
          title: 'Статистика - TDH Promodo'
        }
      }
    ]
  },

  // Перенаправления
  {
    path: '/dashboard',
    redirect: '/app'
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundPage,
    meta: {
      title: 'Сторінку не знайдено - TDH Promodo',
      public: true
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Глобальные navigation guards
router.beforeEach((to, from, next) => {
  // Проверка аутентификации
  const isAuthenticated = localStorage.getItem('auth_token')
  const userRole = localStorage.getItem('user_role')
  
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }
  
  if (to.meta.requiresAdmin && userRole !== 'admin') {
    next({ name: 'dashboard' })
    return
  }
  
  if (to.name === 'login' && isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }
  
  // Установка заголовка страницы
  if (to.meta.title) {
    document.title = to.meta.title as string
  }
  
  next()
})

// Обработка ошибок навигации
router.onError((error) => {
  console.error('Router error:', error)
})

export default router 